import { TradeSide } from '../types';

export const computeBuyScale = ({
  totalBuyCents,
  totalSellCents,
  cashAvailableForTradeCents,
}: {
  totalBuyCents: number;
  totalSellCents: number;
  cashAvailableForTradeCents: number;
}): number => {
  const buyingPower = cashAvailableForTradeCents + totalSellCents;
  if (totalBuyCents > 0 && totalBuyCents > buyingPower) {
    return buyingPower / totalBuyCents;
  }
  return 1;
};

export const computeAfterValueCents = ({
  currentValueCents,
  side,
  cappedAmountCents,
}: {
  currentValueCents: number;
  side: TradeSide;
  cappedAmountCents: number;
}): number =>
  side === TradeSide.BUY
    ? currentValueCents + cappedAmountCents
    : currentValueCents - cappedAmountCents;

export const buildConflictKey = (
  subAccountId: string,
  financialProductId: string,
): string => `${subAccountId}:${financialProductId}`;

export const isConflictingTrade = (
  trade: { subAccountId: string; financialProductId: string },
  conflictKeys: Set<string>,
): boolean =>
  conflictKeys.has(
    buildConflictKey(trade.subAccountId, trade.financialProductId),
  );

type CurrencyTrade = {
  currency: string;
  side: TradeSide;
  amountCents: number;
};

export type CurrencyTotals = {
  currency: string;
  buyCents: number;
  sellCents: number;
  netCents: number;
};

export const groupTotalsByCurrency = (
  trades: CurrencyTrade[],
): CurrencyTotals[] => {
  const byCurrency = new Map<string, CurrencyTotals>();

  for (const trade of trades) {
    const existing = byCurrency.get(trade.currency) ?? {
      currency: trade.currency,
      buyCents: 0,
      sellCents: 0,
      netCents: 0,
    };
    if (trade.side === TradeSide.BUY) {
      existing.buyCents += trade.amountCents;
    } else {
      existing.sellCents += trade.amountCents;
    }
    byCurrency.set(trade.currency, existing);
  }

  return Array.from(byCurrency.values())
    .map((totals) => ({
      ...totals,
      netCents: totals.sellCents - totals.buyCents,
    }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
};
